import axios from "axios";
import {
  Avatar,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import ITicket from "../@type";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../scripts/context/App";
import toast from "react-hot-toast";

interface IMessage {
  attachment?: string;
  isMine: boolean;
  message: string;
  message_id: number;
  sent_time: string;
  user_avatar: string;
  user_name: string;
}

interface ICategory {
  id: number | string;
  name: string;
}

interface IData {
  ticket_id: number;
  category: ICategory;
  title: string;
  sent_time: string;
  messages: IMessage[];
}

type IProps = {
  ticketSenderData: ITicket;
  ticket: IData;
  setTicket: any;
};

export default function TicketHeader({
  ticketSenderData,
  setTicket,
  ticket,
}: IProps) {
  const { user } = useContext(AppContext);
  const [categories, setCategories] = useState<any[]>([]);

  const handleChange = (event: SelectChangeEvent) => {
    axios.post("/support-tickets/update-category", {
      ticket_id: ticket.ticket_id,
      category_id: event.target.value,
    });

    setTicket({
      ...ticket,
      category: {
        id: event.target.value,
        name: categories.find((category) => category.id === event.target.value)
          ?.name,
      },
    });
  };

  const getCategories = async () => {
    if (!user.permissions.includes("view_support_category"))
      return toast.error("You don't have permission to view ticket category.");

    try {
      const {
        data: { data: responseData },
      } = await axios.get("/support-tickets/get-categories");

      setCategories(responseData);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          "Something went wrong. Please try again later."
      );

      console.log(error);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  return (
    <Box
      sx={{
        p: 1,
        borderBottom: "1px solid #e0e0e0",
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        <Avatar
          alt={ticketSenderData.user_name}
          src={ticketSenderData.user_avatar}
          sx={{
            width: "35px",
            height: "35px",
          }}
        />
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontSize: "12px" }}>
              {ticketSenderData.user_name}
            </Typography>
            {ticketSenderData.sent_time && (
              <>
                -
                <Typography variant="subtitle1" sx={{ fontSize: "10px" }}>
                  {ticketSenderData.sent_time}
                </Typography>
              </>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{ fontSize: "11px", wordBreak: "break-all" }}
          >
            {ticket.title}
          </Typography>
        </Box>
      </Box>

      <FormControl variant="standard" sx={{ minWidth: 200 }}>
        <InputLabel id="category-simple-select-filled-label">
          Change Category
        </InputLabel>
        <Select
          labelId="category-simple-select-filled-label"
          id="category-simple-select-filled"
          value={ticket.category?.id?.toString()}
          onChange={handleChange}
          size="small"
          disabled={
            !user.permissions.includes("update_ticket_category") ||
            !ticket.ticket_id
          }
        >
          <MenuItem value={0}>
            <em>Select New Category</em>
          </MenuItem>
          {categories.length > 0 ? (
            categories.map((category: ICategory) => (
              <MenuItem value={category.id}>{category.name}</MenuItem>
            ))
          ) : (
            <MenuItem value={1}>
              <em>No Categories Found</em>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
}
